import {FC} from "react";
import {Tooltip} from "react-tooltip";
import InfoIcon from "../../../../assets/icons/info-italic.svg";
import {IGartnerAnalyticsData} from "../../../../pages/Dashboard/Dashboard.type";
import {
  GARTNER_CHART_COLORS,
  GARTNER_INFO_TEXT,
  formatGartnerGrowthRate,
  formatGartnerLagYears,
  formatGartnerWeights,
} from "./constants";

interface IProps {
  analytics: IGartnerAnalyticsData;
}

const METRIC_ROWS: Array<{
  label: string;
  getValue: (analytics: IGartnerAnalyticsData) => string;
}> = [
  {
    label: "Фаза жизненного цикла",
    getValue: (analytics) => analytics.phase_2025,
  },
  {
    label: "t* (задержка наука -> патенты)",
    getValue: (analytics) => formatGartnerLagYears(analytics.patent_lag_from_science),
  },
  {
    label: "Пик технологического развития (Кривая H(t))",
    getValue: (analytics) => analytics.h_t_peak,
  },
  {
    label: "Интенсивность роста научного интереса",
    getValue: (analytics) => formatGartnerGrowthRate(analytics.growth_rate),
  },
  {
    label: "Вклад научной / патентной активности",
    getValue: (analytics) => {
      const science = analytics.weights_sci_pat?.science;
      const patents = analytics.weights_sci_pat?.patents;
      if (science == null || patents == null) {
        return "—";
      }
      return formatGartnerWeights(science, patents);
    },
  },
];

export const GartnerMetricsPanel: FC<IProps> = ({analytics}) => (
  <div
    style={{
      border: `1px solid ${GARTNER_CHART_COLORS.panelBorder}`,
      borderRadius: "12px",
      overflow: "hidden",
      backgroundColor: GARTNER_CHART_COLORS.panelBg,
    }}
  >
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
        width: "100%",
      }}
    >
      {METRIC_ROWS.map((row, index) => (
        <div
          key={row.label}
          style={{
            padding: "10px 8px",
            borderRight:
              index < METRIC_ROWS.length - 1
                ? `1px solid ${GARTNER_CHART_COLORS.panelBorder}`
                : undefined,
          }}
        >
          <div
            style={{
              fontSize: "10px",
              color: "#9CA3AF",
              lineHeight: "14px",
              marginBottom: "6px",
              minHeight: "42px",
            }}
          >
            {row.label}
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "#FFFFFF",
              lineHeight: "18px",
              fontWeight: 500,
              wordBreak: "break-word",
            }}
          >
            {row.getValue(analytics)}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export type GartnerEntityType = "technology" | "cluster";

interface IHeaderProps {
  entityType?: GartnerEntityType;
  entityName: string;
  clusterName?: string;
}

export const GartnerPanelHeader: FC<IHeaderProps> = ({
  entityType = "technology",
  entityName,
  clusterName,
}) => (
  <div style={{display: "flex", flexDirection: "column", gap: "8px"}}>
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "12px",
      }}
    >
      <h2
        style={{
          margin: 0,
          color: "#FFFFFF",
          fontSize: "18px",
          fontWeight: 600,
          lineHeight: "24px",
        }}
      >
        {entityType === "cluster"
          ? `Модель жизненного цикла кластера «${entityName}»`
          : `Модель жизненного цикла технологии «${entityName}»`}
      </h2>
      <button
        type="button"
        aria-label="Справка о модели жизненного цикла"
        data-tooltip-id="gartner-info-tooltip"
        data-tooltip-content={GARTNER_INFO_TEXT}
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <img src={InfoIcon} alt="" width={20} height={20} />
      </button>
      <Tooltip
        id="gartner-info-tooltip"
        className="tooltip"
        noArrow
        offset={4}
        style={{maxWidth: 360, whiteSpace: "pre-line"}}
      />
    </div>
    {entityType === "technology" && clusterName && (
      <div style={{color: "#9CA3AF", fontSize: "14px", lineHeight: "20px"}}>
        {`Кластер «${clusterName}»`}
      </div>
    )}
  </div>
);
